/**
 * @module engine/export/pdf/schedule
 * @summary Build and draw the equipment schedule that goes on its own sheet.
 * @description
 *   A supplier quotes from a LIST, never from a picture. They will find the
 *   mixer on the layout, then look for the line that tells them its tag, what it
 *   is, how many there are and roughly how big — and if the drawing has no such
 *   line they will email and ask, which costs a day per question.
 *
 *   Rows come straight off the drawing, so the schedule cannot disagree with the
 *   layout it is bound to:
 *   - a shape with a **tag** gets its own row, because a tag is a promise that
 *     this specific item is distinguishable from its twins;
 *   - untagged shapes are **grouped** by type and label and counted, because
 *     eight identical untagged bag stations are one line item with a QTY of 8,
 *     not eight rows a reader has to notice are the same;
 *   - annotations (zone boxes, text, arrows, the title-block symbol) are left
 *     out — they are how the drawing talks, not things anyone can buy.
 *
 *   Sizes are printed in metres, converted through the document's mm-per-unit
 *   scale, and marked approximate: they are the footprint the engineer drew, not
 *   a vendor dimension, and labelling them otherwise would invite someone to
 *   fabricate against them.
 */
import type { jsPDF } from 'jspdf';
import type { Shape } from '../../../types';
import { DEFS } from '../../shapes/defs';
import type { Rect } from './sheet';

/** One line of the schedule. */
export interface ScheduleRow {
  tag: string;
  description: string;
  /** The library name for the symbol — what kind of thing it is. */
  kind: string;
  qty: number;
  /** Plan footprint, `'2.5 × 1.2'` in metres, or `''` when it cannot be known. */
  sizeM: string;
  /** The 3D floor it stands on, when the drawing says. */
  level: string;
}

/** Shapes that are annotation rather than equipment never reach the schedule. */
function isEquipment(s: Shape): boolean {
  const def = DEFS[s.type];
  if (def && def.g === 'Annotations') return false;
  // An unknown type is more likely a custom symbol for real equipment than an
  // annotation, so it stays in — a missing line item is worse than a spare one.
  return true;
}

/**
 * Collapse the drawing into schedule rows.
 * @param shapes The document's shapes.
 * @param scaleMm Millimetres per drawing unit.
 * @param levelName Resolves a shape's `level` id to its display name.
 */
export function buildSchedule(
  shapes: Shape[],
  scaleMm: number,
  levelName: (id: string | undefined) => string,
): ScheduleRow[] {
  const rows: ScheduleRow[] = [];
  /** Untagged items keyed by what makes them interchangeable. */
  const grouped = new Map<string, ScheduleRow>();

  for (const s of shapes) {
    if (!isEquipment(s)) continue;
    const kind = DEFS[s.type]?.label || prettyType(s.type);
    const description = (s.label || '').trim() || kind;
    const sizeM = footprint(s, scaleMm);
    const level = levelName(s.level);
    const tag = (s.tag || '').trim();

    if (tag) {
      rows.push({ tag, description, kind, qty: 1, sizeM, level });
      continue;
    }
    const key = `${s.type} ${description} ${sizeM} ${level}`;
    const hit = grouped.get(key);
    if (hit) hit.qty += 1;
    else grouped.set(key, { tag: '', description, kind, qty: 1, sizeM, level });
  }

  rows.push(...grouped.values());
  // Tagged items first and in tag order, so the reader can find a tag by
  // scanning; then the counted groups alphabetically.
  return rows.sort((a, b) => {
    if (!!a.tag !== !!b.tag) return a.tag ? -1 : 1;
    if (a.tag) return natural(a.tag, b.tag);
    return a.description.localeCompare(b.description) || a.kind.localeCompare(b.kind);
  });
}

/** `'E-9'` before `'E-10'` — the order a human reads tags in. */
function natural(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function prettyType(t: string): string {
  return (t || 'Item').replace(/[-_]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

/** Plan footprint in metres, from drawing units through the document scale. */
function footprint(s: Shape, scaleMm: number): string {
  if (!(scaleMm > 0) || !(s.w > 0) || !(s.h > 0)) return '';
  // A rotated item occupies the same footprint, turned — report it as drawn.
  const wM = (s.w * scaleMm) / 1000;
  const hM = (s.h * scaleMm) / 1000;
  return `${round1(wM)} × ${round1(hM)}`;
}

function round1(v: number): string {
  return String(Number(v.toFixed(v < 10 ? 2 : 1)));
}

interface Col {
  key: keyof ScheduleRow | 'no';
  head: string;
  /** Share of the table width. */
  frac: number;
  align: 'left' | 'right' | 'center';
}

const COLS: Col[] = [
  { key: 'no', head: 'ITEM', frac: 0.07, align: 'right' },
  { key: 'tag', head: 'TAG', frac: 0.13, align: 'left' },
  { key: 'description', head: 'DESCRIPTION', frac: 0.35, align: 'left' },
  { key: 'kind', head: 'TYPE', frac: 0.21, align: 'left' },
  { key: 'qty', head: 'QTY', frac: 0.07, align: 'right' },
  { key: 'sizeM', head: 'SIZE (m)', frac: 0.11, align: 'right' },
  { key: 'level', head: 'LEVEL', frac: 0.06, align: 'left' },
];

const INK = { r: 26, g: 26, b: 26 };
const BRAND = { r: 43, g: 87, b: 154 };

/** Header band, body row and footnote heights in mm — the table's whole geometry. */
const HEAD_H = 7;
const ROW_H = 5.6;
const NOTE_H = 6;

/**
 * How many rows fit in an area of height `areaH`. The sheet COUNT has to be
 * known before the first sheet is drawn (a title block that says "1 of 3" must
 * not be guessing), so the pagination arithmetic lives here and both the
 * counter and the renderer call it.
 */
export function scheduleCapacity(areaH: number, hasNote: boolean): number {
  return Math.max(1, Math.floor((areaH - HEAD_H - (hasNote ? NOTE_H : 0)) / ROW_H));
}

/**
 * Draw as much of the schedule as fits in `area`, and report where it stopped
 * so the caller can start a fresh sheet from there.
 * @returns The index of the first row that did NOT fit (`rows.length` if done).
 */
export function drawSchedule(
  doc: jsPDF,
  area: Rect,
  rows: ScheduleRow[],
  from: number,
  note: string,
): number {
  const headH = HEAD_H;
  const rowH = ROW_H;
  const xs: number[] = [];
  let acc = area.x;
  for (const c of COLS) {
    xs.push(acc);
    acc += c.frac * area.w;
  }
  xs.push(area.x + area.w);

  doc.setLineDashPattern([], 0);
  // Header band.
  doc.setFillColor(BRAND.r, BRAND.g, BRAND.b);
  doc.rect(area.x, area.y, area.w, headH, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  COLS.forEach((c, i) => {
    doc.text(c.head, textX(xs[i], xs[i + 1], c.align), area.y + headH * 0.68, {
      align: c.align,
    });
  });

  const bodyTop = area.y + headH;
  const end = Math.min(rows.length, from + scheduleCapacity(area.h, !!note));

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  for (let i = from; i < end; i++) {
    const r = rows[i];
    const ry = bodyTop + (i - from) * rowH;
    // Zebra banding: a 300-row schedule is read across, and the eye needs help.
    if ((i - from) % 2 === 1) {
      doc.setFillColor(246, 247, 249);
      doc.rect(area.x, ry, area.w, rowH, 'F');
    }
    doc.setTextColor(INK.r, INK.g, INK.b);
    COLS.forEach((c, ci) => {
      const raw =
        c.key === 'no' ? String(i + 1) : c.key === 'qty' ? String(r.qty) : String(r[c.key] ?? '');
      if (!raw) return;
      doc.setFont('helvetica', c.key === 'tag' ? 'bold' : 'normal');
      const maxW = xs[ci + 1] - xs[ci] - 3;
      doc.text(clip(doc, raw, maxW), textX(xs[ci], xs[ci + 1], c.align), ry + rowH * 0.72, {
        align: c.align,
      });
    });
  }

  // Rules: verticals full height of what was drawn, plus a closing line.
  const bodyH = (end - from) * rowH;
  doc.setDrawColor(200, 204, 210);
  doc.setLineWidth(0.15);
  for (let i = 1; i < COLS.length; i++) doc.line(xs[i], area.y, xs[i], bodyTop + bodyH);
  doc.setDrawColor(INK.r, INK.g, INK.b);
  doc.setLineWidth(0.3);
  doc.rect(area.x, area.y, area.w, headH + bodyH);

  if (note) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.2);
    doc.setTextColor(120, 120, 120);
    doc.text(note, area.x, bodyTop + bodyH + 4);
  }
  return end;
}

function textX(x0: number, x1: number, align: Col['align']): number {
  if (align === 'right') return x1 - 1.5;
  if (align === 'center') return (x0 + x1) / 2;
  return x0 + 1.5;
}

/** Truncate to the column width at whatever font is currently set. */
function clip(doc: jsPDF, txt: string, maxW: number): string {
  if (doc.getTextWidth(txt) <= maxW) return txt;
  let lo = 0;
  let hi = txt.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (doc.getTextWidth(txt.slice(0, mid) + '…') <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return txt.slice(0, lo) + '…';
}

/** A CSV of the same rows, for the supplier who would rather have a spreadsheet. */
export function scheduleCsv(rows: ScheduleRow[]): string {
  const head = ['ITEM', 'TAG', 'DESCRIPTION', 'TYPE', 'QTY', 'WIDTH_M', 'DEPTH_M', 'LEVEL'];
  const q = (v: string | number) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [head.join(',')];
  rows.forEach((r, i) => {
    const [w = '', d = ''] = r.sizeM ? r.sizeM.split('×').map((s) => s.trim()) : [];
    lines.push([i + 1, r.tag, r.description, r.kind, r.qty, w, d, r.level].map(q).join(','));
  });
  return lines.join('\r\n');
}
